import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { RequestHistoryDomain } from '../lib/request-history';
import { LookupLocale, LookupSaveResponse } from '../types';

type Props = {
  domain: RequestHistoryDomain;
  code: string;
  branchNumber?: number;
  area: string;
  itemKey?: string;
  value: LookupLocale;
  hasRecord: boolean;
  autoTranslates: boolean;
};

// one locale of a looked-up field, showing the product / category's own text (blank when it has none);
// saves go through the translation override flow, never the product / category itself
export const LookupTranslationLine = ({ domain, code, branchNumber, area, itemKey, value, hasRecord, autoTranslates }: Props) => {
  const { authFetch } = useAuth({ scopes: [] });

  const [saved, setSaved] = useState(value.liveText ?? '');
  const [text, setText] = useState(value.liveText ?? '');
  const [pendingSync, setPendingSync] = useState(false);
  const [showLive, setShowLive] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      const response = await authFetch('/api/overrides/lookup', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain, code, branchNumber, area, itemKey, locale: value.locale, text }),
      });
      if (!response.ok) {
        const responseBody: unknown = await response.json().catch(() => response.statusText);
        throw new Error(typeof responseBody === 'string' ? responseBody : response.statusText);
      }
      return (await response.json()) as LookupSaveResponse;
    },
    // the doc only picks the saved text up once the domain syncs, so the box keeps it until then
    onSuccess: ({ warning }) => {
      setSaved(text);
      setWarning(warning ?? null);
      setPendingSync(true);
    },
  });

  return (
    <>
      <form
        className="translation-line"
        onSubmit={(e) => {
          e.preventDefault();
          mutate();
        }}
      >
        <input className="locale-label" type="text" value={value.locale} disabled />
        <textarea
          className="translation-text"
          value={showLive ? value.liveText ?? '' : text}
          disabled={showLive}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" disabled={showLive || isPending || !text || text === saved}>
          {isPending ? 'Saving...' : 'Save'}
        </button>
      </form>
      {pendingSync && (
        <p className="lookup-line-status">
          <span className="lookup-pending-badge">Pending sync – live in ~3-5 min</span>
          <label>
            <input type="checkbox" checked={showLive} onChange={(e) => setShowLive(e.target.checked)} />
            Show live value
          </label>
        </p>
      )}
      {!hasRecord && !pendingSync && (
        <p className="lookup-hint">
          {autoTranslates
            ? 'No translation record – saving creates it and auto-translates other locales'
            : 'No translation record – saving creates it for this locale only, sizes like this are never auto-translated'}
        </p>
      )}
      {warning && <p className="lookup-hint">{warning}</p>}
      {error && <p className="error">{error.message}</p>}
    </>
  );
};
